/**
 * ProfileOnboardingForm v2 — avec PesacoreArchitect intégré
 *
 * Diff vs v1 :
 *   - Colonne droite : PesacoreArchitect remplace ConfigPreview statique
 *     → l'IA s'amorce dès qu'un persona est sélectionné
 *   - onAction(action) → handlers locaux pour les actions actionnables
 *     (création CRM, génération PDF, pré-remplissage de dossier)
 *   - ConfigPreview reste en fallback mobile (l'IA n'est visible que lg:)
 *   - Le composant PesacoreArchitect reçoit en temps réel :
 *     profileId, personaPath, formData → elle s'adapte à chaque changement
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Brain,
  Check,
  ChevronDown,
  ChevronRight,
  Layers, Settings2
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getProfile } from '../../config/activityProfileConfig2';
import useGrandeEcole from '../../stores/useGrandeEcole';
import PesacoreArchitect from './PesacoreArchitect';

// ── Utilitaires (identiques à v1) ────────────────────────────────────────────

function flattenPath(tree = [], path = []) {
  const labels = [];
  let nodes = tree;
  for (const key of path) {
    const node = nodes?.find(n => n.key === key);
    if (!node) break;
    labels.push(node.label);
    nodes = node.children ?? [];
  }
  return labels;
}

const leafKey = path => path[path.length - 1] ?? null;

function normalizeTree(tree = []) {
  return tree.map(node => ({
    icon: '◈', meta: '',
    ...node,
    children: normalizeTree(node.children ?? []),
  }));
}

function filterFieldsForPersona(fields = [], currentLeaf) {
  return fields.filter(f => {
    if (!f.showForPersonas) return true;
    if (!currentLeaf) return false;
    return f.showForPersonas.includes(currentLeaf);
  });
}

// ── ProgressRail ─────────────────────────────────────────────────────────────

function ProgressRail({ steps, current }) {
  return (
    <div className="flex items-center mb-10">
      {steps.map((s, i) => {
        const done = i < current, active = i === current;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            <div className="relative flex flex-col items-center">
              <div
                className="w-7 h-7 flex items-center justify-center text-[10px] font-mono transition-all duration-300"
                style={{
                  background : done ? 'rgba(34,211,238,0.15)' : active ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.03)',
                  border     : done ? '1px solid rgba(34,211,238,0.5)' : active ? '1px solid rgba(245,158,11,0.5)' : '1px solid rgba(255,255,255,0.08)',
                  color      : done ? '#22d3ee' : active ? '#f59e0b' : '#52525b',
                }}
              >
                {done ? <Check size={12} /> : i + 1}
              </div>
              <span
                className="absolute top-9 text-[9px] uppercase tracking-widest whitespace-nowrap"
                style={{ color: active ? '#f59e0b' : done ? 'rgba(34,211,238,0.6)' : '#3f3f46' }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="flex-1 h-px mx-1 transition-all duration-500"
                style={{ background: done ? 'rgba(34,211,238,0.25)' : 'rgba(255,255,255,0.05)' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── TreeNode ──────────────────────────────────────────────────────────────────

function TreeNode({ node, depth, selectedPath, onSelectPath }) {
  const isSelected  = selectedPath[depth] === node.key;
  const isOnPath    = selectedPath.includes(node.key);
  const hasChildren = node.children?.length > 0;
  const [open, setOpen] = useState(isOnPath);

  return (
    <div>
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-all duration-150"
        style={{
          marginLeft : depth * 20,
          background : isSelected ? 'rgba(245,158,11,0.06)' : 'transparent',
          borderLeft : isSelected ? '2px solid rgba(245,158,11,0.5)' : '2px solid transparent',
        }}
        onClick={() => {
          onSelectPath([...selectedPath.slice(0, depth), node.key]);
          if (hasChildren && !open) setOpen(true);
        }}
      >
        <div
          className="w-4 h-4 flex-shrink-0 flex items-center justify-center transition-all"
          style={{
            border     : isSelected ? '1px solid rgba(245,158,11,0.7)' : '1px solid rgba(255,255,255,0.12)',
            background : isSelected ? 'rgba(245,158,11,0.2)' : 'transparent',
          }}
        >
          {isSelected && <div className="w-1.5 h-1.5 bg-amber-400" />}
        </div>
        <span className="text-base flex-shrink-0">{node.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-mono" style={{ color: isSelected ? '#f59e0b' : '#d4d4d8' }}>{node.label}</div>
          {node.meta && <div className="text-[10px] mt-0.5 text-zinc-600">{node.meta}</div>}
        </div>
        {hasChildren && (
          <button type="button" onClick={e => { e.stopPropagation(); setOpen(!open); }} style={{ color: open ? '#22d3ee' : '#3f3f46' }}>
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        )}
      </div>
      <AnimatePresence>
        {hasChildren && open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }}>
            {node.children.map(child => (
              <TreeNode key={child.key} node={child} depth={depth + 1} selectedPath={selectedPath} onSelectPath={onSelectPath} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── StepPersona ───────────────────────────────────────────────────────────────

function StepPersona({ personaConfig, selectedPath, onSelectPath, error }) {
  const tree = normalizeTree(personaConfig.tree ?? []);
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-mono uppercase tracking-widest text-amber-500/70 mb-1">{personaConfig.label}</h3>
        {personaConfig.description && <p className="text-xs text-zinc-500">{personaConfig.description}</p>}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 300, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}>
        {tree.map(node => (
          <TreeNode key={node.key} node={node} depth={0} selectedPath={selectedPath} onSelectPath={onSelectPath} />
        ))}
      </div>
      {error && <p className="text-[10px] font-mono text-red-400/80">{error}</p>}
    </div>
  );
}

// ── StepFields ────────────────────────────────────────────────────────────────

function StepFields({ fields, formData, onChange, onboardingConfig }) {
  const regularFields  = fields.filter(f => f.type !== 'checkbox');
  const checkboxFields = fields.filter(f => f.type === 'checkbox');

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xs font-mono uppercase tracking-widest text-amber-500/70 mb-1">{onboardingConfig?.title ?? 'Votre organisation'}</h3>
        <p className="text-xs text-zinc-500">{onboardingConfig?.description ?? 'Ces informations personnalisent vos indicateurs.'}</p>
      </div>

      {regularFields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
          {regularFields.map(field => (
            <div key={field.key}>
              <label className="block text-[9px] font-mono uppercase tracking-widest mb-1.5" style={{ color: 'rgba(34,211,238,0.4)' }}>
                {field.label}{field.required && <span className="text-amber-500/60 ml-1">*</span>}
              </label>
              {['text','email','number','date'].includes(field.type) && (
                <input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  className="w-full bg-black/40 px-3 py-2.5 text-sm font-mono text-white outline-none transition-all"
                  style={{ border: '1px solid rgba(34,211,238,0.1)', caretColor: '#f59e0b' }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(34,211,238,0.35)')}
                  onBlur={e  => (e.target.style.borderColor = 'rgba(34,211,238,0.1)')}
                />
              )}
              {field.type === 'select' && (
                <select
                  value={formData[field.key] || ''}
                  onChange={e => onChange(field.key, e.target.value)}
                  className="w-full bg-black/40 px-3 py-2.5 text-sm font-mono text-white outline-none appearance-none transition-all"
                  style={{ border: '1px solid rgba(34,211,238,0.1)' }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(34,211,238,0.35)')}
                  onBlur={e  => (e.target.style.borderColor = 'rgba(34,211,238,0.1)')}
                >
                  <option value="" className="bg-[#020617]">Sélectionner…</option>
                  {(field.options ?? []).map(o => <option key={o} value={o} className="bg-[#020617]">{o}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      {checkboxFields.length > 0 && (
        <div className="space-y-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mb-3">Déclarations obligatoires</div>
          {checkboxFields.map(field => (
            <label key={field.key} className="flex items-start gap-3 cursor-pointer group">
              <div
                className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                style={{
                  border     : formData[field.key] ? '1px solid rgba(34,211,238,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  background : formData[field.key] ? 'rgba(34,211,238,0.12)' : 'transparent',
                }}
                onClick={() => onChange(field.key, !formData[field.key])}
              >
                {formData[field.key] && <Check size={10} className="text-cyan-400" />}
              </div>
              <span className="text-[11px] font-mono leading-relaxed text-zinc-400 group-hover:text-zinc-200 transition-colors">
                {field.label}{field.required && <span className="text-amber-500/60 ml-1">*</span>}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── StepConfirm ───────────────────────────────────────────────────────────────

function StepConfirm({ selectedPath, formData, profile }) {
  const personaTree = normalizeTree(profile?.personas?.tree ?? []);
  const labels      = flattenPath(personaTree, selectedPath);
  const fields      = profile?.onboarding?.fields ?? [];
  const framework   = profile?.certificationFramework ?? {};

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xs font-mono uppercase tracking-widest text-amber-500/70 mb-1">Récapitulatif</h3>
        <p className="text-xs text-zinc-500">Votre espace sera configuré avec ces paramètres dès validation.</p>
      </div>
      <div className="space-y-2">
        {labels.length > 0 && (
          <div className="flex items-start gap-3 px-4 py-3" style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.12)' }}>
            <Layers size={13} className="text-amber-500/60 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mb-1">Structure</div>
              <div className="text-sm font-mono text-amber-400/90">{labels.join(' › ')}</div>
            </div>
          </div>
        )}
        {framework.primary && (
          <div className="flex items-center justify-between px-4 py-2.5" style={{ background: 'rgba(34,211,238,0.03)', border: '1px solid rgba(34,211,238,0.08)' }}>
            <span className="text-[10px] font-mono text-zinc-500">Certification principale</span>
            <span className="text-[10px] font-mono text-cyan-400 uppercase">{framework.primary}</span>
          </div>
        )}
        {Object.entries(formData).filter(([, v]) => v !== '' && v !== false && v != null).map(([k, v]) => {
          const field = fields.find(f => f.key === k);
          return (
            <div key={k} className="flex items-center justify-between px-4 py-2.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
              <span className="text-[10px] font-mono text-zinc-500">{field?.label ?? k}</span>
              <span className="text-[10px] font-mono text-zinc-300">{typeof v === 'boolean' ? '✓ Oui' : v}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'rgba(34,211,238,0.04)', border: '1px solid rgba(34,211,238,0.08)' }}>
        <BarChart3 size={11} className="text-cyan-500/40 flex-shrink-0" />
        <p className="text-[10px] font-mono text-zinc-500">
          Votre dashboard, modules et documents réglementaires seront personnalisés immédiatement.
        </p>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function ProfileOnboardingForm({ profileId, onComplete, onSkip }) {
  const profile          = getProfile(profileId);
  const personaConfig    = profile?.personas;
  const onboardingConfig = profile?.onboarding;
  const allFields        = onboardingConfig?.fields ?? [];

  const { tenantId, tenantOnboardingData, updateTenantOnboarding } = useGrandeEcole();

  const [currentStep,  setCurrentStep]  = useState(0);
  const [selectedPath, setSelectedPath] = useState([]);
  const [formData,     setFormData]     = useState({});
  const [personaError, setPersonaError] = useState('');
  const [loading,      setLoading]      = useState(false);
  const [done,         setDone]         = useState(false);

  // Pré-remplissage
  useEffect(() => {
    if (!tenantOnboardingData) return;
    if (tenantOnboardingData.profileId === profileId && Array.isArray(tenantOnboardingData.personaPath) && tenantOnboardingData.personaPath.length > 0) {
      setSelectedPath(tenantOnboardingData.personaPath);
    }
    const reserved = new Set(['profileId', 'personaPath']);
    const restored = Object.fromEntries(Object.entries(tenantOnboardingData).filter(([k]) => !reserved.has(k)));
    if (Object.keys(restored).length > 0) setFormData(restored);
  }, [tenantOnboardingData, profileId]);

  const currentLeaf   = leafKey(selectedPath);
  const visibleFields = filterFieldsForPersona(allFields, currentLeaf);

  const hasPersona = !!personaConfig?.tree?.length;
  const hasFields  = allFields.length > 0;

  const steps = [
    hasPersona && { key: 'persona', label: 'Profil'     },
    hasFields  && { key: 'details', label: 'Détails'    },
                  { key: 'confirm', label: 'Validation' },
  ].filter(Boolean);

  const handleChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (step?.key === 'persona') {
      if (personaConfig?.required && selectedPath.length === 0) {
        setPersonaError('Veuillez sélectionner votre type de structure.');
        return false;
      }
    }
    if (step?.key === 'details') {
      const required = visibleFields.filter(f => f.required);
      for (const field of required) {
        const val = formData[field.key];
        if (val === undefined || val === null || val === '' || val === false) {
          toast.error(`Le champ "${field.label}" est obligatoire.`);
          return false;
        }
      }
    }
    return true;
  };

  const canProceed = () => {
    const step = steps[currentStep];
    if (step?.key === 'persona') return selectedPath.length > 0;
    if (step?.key === 'details') {
      return visibleFields.filter(f => f.required).every(f => {
        const v = formData[f.key];
        return v !== undefined && v !== null && v !== '' && v !== false;
      });
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setPersonaError('');
    setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    setPersonaError('');
    setCurrentStep(s => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    setLoading(true);
    try {
      await updateTenantOnboarding(tenantId, { profileId, personaPath: selectedPath, ...formData });
      toast.success('Configuration enregistrée avec succès.');
      setDone(true);
      setTimeout(() => onComplete?.(), 1200);
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gestion des actions déclenchées depuis PesacoreArchitect
   * L'IA peut demander de :
   *   - créer un contact CRM ('crm')
   *   - générer un PDF ('pdf')
   *   - pré-remplir un dossier ('dossier') → màj formData
   *   - ouvrir une config ('config')
   */
  const handleArchitectAction = (action) => {
    switch (action.type) {
      case 'dossier':
        // Pré-remplit les champs du formulaire avec les données générées par l'IA
        if (action.payload && typeof action.payload === 'object') {
          setFormData(prev => ({ ...prev, ...action.payload }));
          toast.success('Champs pré-remplis par PESACORE Architect');
          // Si on est sur persona, on avance vers details
          const detailsIdx = steps.findIndex(s => s.key === 'details');
          if (detailsIdx > currentStep) setCurrentStep(detailsIdx);
        }
        break;
      case 'crm':
        toast.info('Création CRM — disponible depuis le dashboard');
        break;
      case 'pdf':
        toast.info('Génération PDF — disponible après validation');
        break;
      default:
        break;
    }
  };

  const isLast = currentStep === steps.length - 1;

  return (
    <div
      className="min-h-screen bg-[#020617] text-cyan-50 flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: 'ui-monospace, monospace' }}
    >
      {/* Fond */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(rgba(34,211,238,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_25%_20%,rgba(34,211,238,0.06),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_75%_80%,rgba(245,158,11,0.04),transparent_55%)]" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-6xl"
      >
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Settings2 size={13} className="text-amber-500/50" />
            <span className="text-[9px] font-mono uppercase tracking-widest text-amber-500/40">{profile?.label ?? 'Configuration'}</span>
          </div>
          <h2 className="font-black uppercase tracking-tight text-white mb-2" style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(1.2rem, 3.5vw, 1.6rem)' }}>
            {onboardingConfig?.title ?? 'Configuration de votre espace'}
          </h2>
          {onboardingConfig?.description && (
            <p className="text-xs text-zinc-500 italic">{onboardingConfig.description}</p>
          )}
        </div>

        <ProgressRail steps={steps} current={currentStep} />

        {/* Layout 3 colonnes : formulaire | IA */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">

          {/* Panneau formulaire */}
          <div
            className="relative p-6 sm:p-8"
            style={{ background: 'rgba(2,6,23,0.85)', border: '1px solid rgba(34,211,238,0.08)', boxShadow: '0 0 60px rgba(34,211,238,0.04)' }}
          >
            <span className="absolute top-0 left-0 w-4 h-4 border-t border-l border-cyan-500/25" />
            <span className="absolute top-0 right-0 w-4 h-4 border-t border-r border-cyan-500/25" />
            <span className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-amber-500/20" />
            <span className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-amber-500/20" />

            <AnimatePresence mode="wait">
              {done ? (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="w-12 h-12 flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.3)' }}>
                    <Check size={20} className="text-cyan-400" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-mono text-cyan-400 mb-1">Configuration enregistrée</div>
                    <div className="text-[10px] font-mono text-zinc-500">Redirection vers votre dashboard…</div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key={currentStep} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>
                  {steps[currentStep]?.key === 'persona' && hasPersona && (
                    <StepPersona
                      personaConfig={personaConfig}
                      selectedPath={selectedPath}
                      onSelectPath={path => { setSelectedPath(path); setPersonaError(''); }}
                      error={personaError}
                    />
                  )}
                  {steps[currentStep]?.key === 'details' && (
                    <StepFields
                      fields={visibleFields.length > 0 ? visibleFields : allFields}
                      formData={formData}
                      onChange={handleChange}
                      onboardingConfig={onboardingConfig}
                    />
                  )}
                  {steps[currentStep]?.key === 'confirm' && (
                    <StepConfirm selectedPath={selectedPath} formData={formData} profile={profile} />
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {!done && (
              <div className="flex items-center justify-between pt-8 mt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex items-center gap-4">
                  {currentStep > 0 && (
                    <button type="button" onClick={handleBack} className="px-4 py-2 text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                      ← Retour
                    </button>
                  )}
                  {onSkip && (
                    <button type="button" onClick={onSkip} className="text-[10px] font-mono text-zinc-600 hover:text-zinc-400 transition-colors">Plus tard</button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={isLast ? handleSubmit : handleNext}
                  disabled={loading || !canProceed()}
                  className="group relative flex items-center gap-2 px-6 py-2.5 text-xs font-mono font-black uppercase tracking-wider text-white overflow-hidden transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)' }}
                >
                  <motion.span
                    className="absolute inset-0 opacity-0 group-hover:opacity-100"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.1), transparent)' }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                  />
                  <span className="relative">{loading ? 'Enregistrement…' : isLast ? 'Valider la configuration' : 'Continuer'}</span>
                  {!loading && !isLast && <ArrowRight size={12} className="relative" />}
                </button>
              </div>
            )}
          </div>

          {/* Panneau IA — PESACORE Architect */}
          <div className="hidden lg:flex flex-col gap-3">
            <div className="flex items-center gap-2 px-1">
              <Brain size={11} className="text-cyan-500/40" />
              <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-700">Architect IH</span>
              {selectedPath.length > 0 && (
                <span className="ml-auto text-[8px] font-mono text-emerald-500/50 flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50 animate-pulse" />
                  Actif
                </span>
              )}
            </div>

            <PesacoreArchitect
              profileId={profileId}
              personaPath={selectedPath}
              formData={formData}
              tenantOnboardingData={tenantOnboardingData}
              onAction={handleArchitectAction}
              className="flex-1"
            />

            {/* Hint */}
            {selectedPath.length === 0 && (
              <div className="text-[9px] font-mono text-zinc-700 text-center px-2 leading-relaxed">
                Sélectionnez votre profil pour activer<br />l'analyse PESACORE en temps réel
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}